import { useState, useEffect } from "react"
import Modal from "../Modal"
import Button from "../Button"
import api from "../../services/api"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import { FAQ_CATEGORIES, FAQ_AUDIENCES } from "../../constants/faqConstants"
import { slugify } from "../../helpers"

const blankForm = () => ({
  slug: "",
  question: "",
  answer: "",
  category: "getting_started",
  audiences: ["everyone"],
  keywords: [],
  featured: false,
  pinned_order: "",
  status: "draft",
  visible_unauth: true,
  related: [],
})

const FAQFormModal = ({ isOpen, onClose, onSuccess, faq = null, allFaqs = [] }) => {
  const isEditing = !!faq
  const [form, setForm] = useState(blankForm)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [keywordInput, setKeywordInput] = useState("")
  const [relatedSearch, setRelatedSearch] = useState("")
  const [relatedOpen, setRelatedOpen] = useState(false)
  const [slugManual, setSlugManual] = useState(false)
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
  const [linkHref, setLinkHref] = useState("")

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
    ],
    content: form.answer,
    onUpdate: ({ editor }) => {
      setForm(prev => ({ ...prev, answer: editor.getHTML() }))
    },
  })

  useEffect(() => {
    if (isOpen) {
      if (faq) {
        setForm({
          slug: faq.slug || "",
          question: faq.question || "",
          answer: faq.answer || "",
          category: faq.category || "getting_started",
          audiences: faq.audiences || ["everyone"],
          keywords: faq.keywords || [],
          featured: faq.featured || false,
          pinned_order: faq.pinned_order || "",
          status: faq.status || "draft",
          visible_unauth: faq.visible_unauth ?? true,
          related: faq.related || [],
        })
        editor?.commands.setContent(faq.answer || "")
      } else {
        setForm(blankForm)
        editor?.commands.setContent("")
      }
      setErrors({})
      setKeywordInput("")
      setRelatedSearch("")
      setRelatedOpen(false)
      setSlugManual(false)
      setLinkPopoverOpen(false)
      setLinkHref("")
    }
  }, [isOpen, faq])

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

  const applyLink = () => {
    const href = linkHref.trim()
    if (href) {
      editor?.chain().focus().extendMarkRange("link").setLink({ href }).run()
    } else {
      editor?.chain().focus().unsetLink().run()
    }
    setLinkPopoverOpen(false)
    setLinkHref("")
  }

  const handleQuestionBlur = () => {
    if (!slugManual && form.question && !isEditing) {
      set("slug", slugify(form.question))
    }
  }

  const handleAddKeyword = () => {
    const val = keywordInput.trim()
    if (val && !form.keywords.includes(val)) {
      set("keywords", [...form.keywords, val])
    }
    setKeywordInput("")
  }

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      handleAddKeyword()
    }
    if (e.key === "Backspace" && !keywordInput && form.keywords.length) {
      set("keywords", form.keywords.slice(0, -1))
    }
  }

  const toggleAudience = (id) => {
    set("audiences", form.audiences.includes(id)
      ? form.audiences.filter(x => x !== id)
      : [...form.audiences, id])
  }

  const addRelated = (id) => {
    if (form.related.length < 3 && !form.related.includes(id)) {
      set("related", [...form.related, id])
    }
    setRelatedOpen(false)
    setRelatedSearch("")
  }

  const validate = () => {
    const e = {}
    if (!form.question.trim()) e.question = "Question is required"
    if (!form.answer || form.answer === "<p></p>") e.answer = "Answer is required"
    if (!form.slug.trim()) e.slug = "Slug is required"
    if (!form.category) e.category = "Category is required"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (statusOverride) => {
    if (!validate()) return
    setSaving(true)
    const payload = {
      ...form,
      status: statusOverride ?? form.status,
      pinned_order: form.pinned_order === "" ? null : Number(form.pinned_order),
    }
    try {
      if (isEditing) {
        await api.updateFAQ(faq.id, payload)
      } else {
        await api.createFAQ(payload)
      }
      onSuccess()
      onClose()
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
      } else {
        setErrors({ submit: error.response?.data?.message || "Failed to save FAQ" })
      }
    } finally {
      setSaving(false)
    }
  }

  const activeCat = FAQ_CATEGORIES.find(c => c.id === form.category)

  const relatedMatches = allFaqs.filter(f =>
    f.id !== faq?.id &&
    !form.related.includes(f.slug) &&
    (!relatedSearch || f.question.toLowerCase().includes(relatedSearch.toLowerCase()))
  ).slice(0, 5)

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-3xl">
      <div className="flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center gap-4 pb-5 border-b border-gray-100">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activeCat?.bg ?? "bg-gray-100"}`}>
            <i className={`fa-solid fa-circle-question text-lg ${activeCat?.color ?? "text-gray-400"}`}></i>
          </div>
          <div>
            <h2 className={`text-xl font-bold ${activeCat?.color ?? "text-gray-900"}`}>
              {isEditing ? "Edit FAQ" : "Add an FAQ"}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditing ? "Changes save on Save Changes." : "New entries default to Draft. Publish when ready."}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 py-5 space-y-5">
          {/* Question */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Question <span className="text-red-500">*</span>
            </label>
            <input
              value={form.question}
              onChange={e => set("question", e.target.value)}
              onBlur={handleQuestionBlur}
              placeholder="e.g. How do I invite a mentor?"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand ${errors.question ? "border-red-400" : "border-gray-200 bg-gray-50"}`}
            />
            {errors.question && <p className="mt-1 text-xs text-red-500">{errors.question}</p>}
          </div>

          {/* Answer — TipTap */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Answer <span className="text-red-500">*</span>
            </label>
            <div className={`rounded-lg border overflow-hidden ${errors.answer ? "border-red-400" : "border-gray-200"}`}>
              {/* Toolbar */}
              <div className="flex items-center gap-1 bg-gray-50 border-b border-gray-200 px-2 py-1">
                <ToolbarBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive("bold")} title="Bold">
                  <i className="fa-solid fa-bold text-xs"></i>
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive("italic")} title="Italic">
                  <i className="fa-solid fa-italic text-xs"></i>
                </ToolbarBtn>
                <div className="w-px h-4 bg-gray-200 mx-1"></div>
                <ToolbarBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive("bulletList")} title="Bullet list">
                  <i className="fa-solid fa-list-ul text-xs"></i>
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive("orderedList")} title="Numbered list">
                  <i className="fa-solid fa-list-ol text-xs"></i>
                </ToolbarBtn>
                <div className="w-px h-4 bg-gray-200 mx-1"></div>
                <ToolbarBtn
                  onClick={() => {
                    setLinkHref(editor?.getAttributes("link").href ?? "")
                    setLinkPopoverOpen(v => !v)
                  }}
                  active={editor?.isActive("link")}
                  title="Link"
                >
                  <i className="fa-solid fa-link text-xs"></i>
                </ToolbarBtn>
                {editor?.isActive("link") && (
                  <ToolbarBtn onClick={() => editor?.chain().focus().unsetLink().run()} title="Remove link">
                    <i className="fa-solid fa-link-slash text-xs"></i>
                  </ToolbarBtn>
                )}
              </div>
              {linkPopoverOpen && (
                <div className="flex items-center gap-2 bg-gray-50 border-b border-gray-200 px-2 py-1.5">
                  <input
                    autoFocus
                    value={linkHref}
                    onChange={e => setLinkHref(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") { e.preventDefault(); applyLink() }
                      if (e.key === "Escape") { setLinkPopoverOpen(false) }
                    }}
                    placeholder="https://…"
                    className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-brand"
                  />
                  <button type="button" onClick={applyLink} className="text-xs font-semibold text-brand px-2 py-1 rounded hover:bg-brand/10">Apply</button>
                  <button type="button" onClick={() => setLinkPopoverOpen(false)} className="text-xs text-gray-400 hover:text-gray-600 px-1">Cancel</button>
                </div>
              )}
              <EditorContent
                editor={editor}
                className="faq-editor min-h-[140px] max-h-[260px] overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus-within:outline-none"
              />
            </div>
            {errors.answer && <p className="mt-1 text-xs text-red-500">{errors.answer}</p>}
          </div>

          {/* Category + Audiences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Category</label>
              <div className="flex flex-wrap gap-1.5">
                {FAQ_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => set("category", cat.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      form.category === cat.id
                        ? `${cat.bg} ${cat.color} ${cat.border}`
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${cat.dot}`}></span>
                    {cat.label}
                  </button>
                ))}
              </div>
              {errors.category && <p className="mt-1 text-xs text-red-500">{errors.category}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Audiences</label>
              <div className="flex flex-wrap gap-1.5">
                {FAQ_AUDIENCES.map(aud => {
                  const active = form.audiences.includes(aud.id)
                  return (
                    <button
                      key={aud.id}
                      type="button"
                      onClick={() => toggleAudience(aud.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? "bg-brandSecondary/10 text-brandSecondary border-brandSecondary"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <i className={`fa-solid ${active ? "fa-check" : "fa-plus"} text-[10px]`}></i>
                      {aud.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Keywords</label>
            <p className="text-xs text-gray-500 mb-2">Searched alongside the question and answer. Press Enter or comma to add.</p>
            <div className="flex flex-wrap gap-1.5 items-center bg-gray-50 border border-gray-200 rounded-lg p-2 min-h-[42px]">
              {form.keywords.map(k => (
                <span key={k} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs font-medium text-gray-700">
                  {k}
                  <button type="button" onClick={() => set("keywords", form.keywords.filter(x => x !== k))} className="text-gray-400 hover:text-gray-600">
                    <i className="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
              ))}
              <input
                value={keywordInput}
                onChange={e => setKeywordInput(e.target.value.replace(/,$/, ""))}
                onKeyDown={handleKeywordKeyDown}
                onBlur={handleAddKeyword}
                placeholder={form.keywords.length ? "" : "Add keywords…"}
                className="flex-1 min-w-[120px] bg-transparent text-sm outline-none py-0.5"
              />
            </div>
          </div>

          {/* Related FAQs */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Related FAQs</label>
            <p className="text-xs text-gray-500 mb-2">Shown under the answer in the help center. Up to 3.</p>
            <div className="space-y-2">
              {form.related.map(slug => {
                const r = allFaqs.find(f => f.slug === slug)
                if (!r) return null
                const cat = FAQ_CATEGORIES.find(c => c.id === r.category)
                return (
                  <div key={slug} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cat?.dot ?? "bg-gray-300"}`}></span>
                    <span className="flex-1 text-sm font-medium text-gray-700 truncate">{r.question}</span>
                    <button type="button" onClick={() => set("related", form.related.filter(x => x !== slug))} className="text-gray-400 hover:text-gray-600">
                      <i className="fa-solid fa-xmark text-xs"></i>
                    </button>
                  </div>
                )
              })}
              {form.related.length < 3 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setRelatedOpen(!relatedOpen)}
                    className="flex items-center gap-2 w-full border border-dashed border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:border-gray-400 bg-white"
                  >
                    <i className="fa-solid fa-plus text-brandSecondary text-xs"></i>
                    Add a related FAQ ({form.related.length}/3)
                  </button>
                  {relatedOpen && (
                    <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2">
                      <input
                        autoFocus
                        value={relatedSearch}
                        onChange={e => setRelatedSearch(e.target.value)}
                        placeholder="Search FAQs…"
                        className="w-full text-sm border-0 bg-gray-50 rounded-md px-3 py-2 mb-1 outline-none"
                      />
                      {relatedMatches.length === 0
                        ? <p className="text-xs text-gray-400 px-2 py-1">No matches</p>
                        : relatedMatches.map(m => {
                          const cat = FAQ_CATEGORIES.find(c => c.id === m.category)
                          return (
                            <button key={m.id} type="button" onClick={() => addRelated(m.slug)}
                              className="flex items-center gap-2 w-full px-2 py-2 text-sm text-left rounded-md hover:bg-gray-50">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cat?.dot ?? "bg-gray-300"}`}></span>
                              <span className="font-medium text-gray-700 truncate">{m.question}</span>
                            </button>
                          )
                        })
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status + Featured */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Status</label>
              <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                {["draft", "published"].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("status", s)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-semibold transition-colors ${
                      form.status === s
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <i className={`fa-solid ${s === "published" ? "fa-circle-check" : "fa-circle-half-stroke"} text-xs ${form.status === s && s === "published" ? "text-green-600" : ""}`}></i>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Featured</label>
              <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                <Toggle on={form.featured} onChange={v => set("featured", v)} />
                <span className="text-sm text-gray-700 flex-1">
                  {form.featured ? 'Pinned to "Most asked"' : "Normal list only"}
                </span>
                {form.featured && (
                  <input
                    type="number"
                    min="1"
                    value={form.pinned_order}
                    onChange={e => set("pinned_order", e.target.value)}
                    placeholder="Order"
                    className="w-16 border border-gray-200 rounded-md px-2 py-1 text-sm bg-white text-center outline-none focus:ring-2 focus:ring-brand"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Visibility</label>
            <div className="flex items-center gap-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg px-4 py-3">
              <i className="fa-solid fa-globe text-blue-500 text-base"></i>
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">Visible to unauthenticated visitors</p>
                <p className="text-xs text-blue-700">The FAQ page lives outside the auth route. Turn off to restrict to logged-in users.</p>
              </div>
              <Toggle on={form.visible_unauth} onChange={v => set("visible_unauth", v)} />
            </div>
          </div>

          {/* Slug + meta */}
          <div className="pt-3 border-t border-dashed border-gray-200 flex items-center gap-5 text-xs text-gray-400">
            <div className="flex items-center gap-1.5">
              <i className="fa-solid fa-hashtag"></i>
              <span>Slug:</span>
              <input
                value={form.slug}
                onChange={e => { set("slug", e.target.value); setSlugManual(true) }}
                placeholder="auto-generated-from-question"
                className={`font-mono bg-gray-50 border rounded px-1.5 py-0.5 text-xs outline-none focus:ring-1 focus:ring-brand ${errors.slug ? "border-red-400" : "border-gray-200"}`}
              />
              {errors.slug && <span className="text-red-500">{errors.slug}</span>}
            </div>
            {isEditing && faq?.updated_at && (
              <span className="flex items-center gap-1">
                <i className="fa-regular fa-clock"></i>
                Updated {new Date(faq.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>

          {errors.submit && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {!isEditing && (
              <Button type="button" variant="secondary-outline" onClick={() => handleSubmit("draft")} disabled={saving}>
                Save Draft
              </Button>
            )}
            <Button type="button" variant="default" onClick={() => handleSubmit(isEditing ? form.status : "published")} disabled={saving}>
              {saving
                ? <><i className="fa fa-spinner fa-spin mr-2"></i>Saving…</>
                : isEditing
                  ? "Save Changes"
                  : "Save & Publish"
              }
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick() }}
      title={title}
      className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
        active ? "bg-gray-200 text-gray-900" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  )
}

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${on ? "bg-brand" : "bg-gray-300"}`}
    >
      <span className={`absolute top-[2px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-all ${on ? "left-[20px]" : "left-[2px]"}`}></span>
    </button>
  )
}

export default FAQFormModal
